import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Users, Plus, Copy, Trophy, Flame,
  TrendingUp, Target, Crown, Palette, Megaphone, Swords, Sparkles,
  Activity, Loader2
} from "lucide-react";
import { toast } from "sonner";
import RivalCard from "@/components/RivalCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAB_LIST = [
  { id: "squad", label: "Squad", icon: Users },
  { id: "drills", label: "Team Drills", icon: Flame },
  { id: "rival", label: "Rival", icon: Swords },
  { id: "identity", label: "Identity", icon: Palette },
];

const DRILL_ICONS = { running: Flame, trending_up: TrendingUp, flame: Flame };

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [drillCatalog, setDrillCatalog] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("squad");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teams/my-teams`, { headers });
      setTeams(res.data.teams || []);
    } catch { /* swallowed */ } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const loadTeam = useCallback(async (teamId) => {
    try {
      const [teamRes, drillsRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}`, { headers }),
        axios.get(`${API}/teams/${teamId}/team-drills`, { headers }),
      ]);
      setSelectedTeam(teamRes.data);
      setDrillCatalog(drillsRes.data.catalog || []);
    } catch { toast.error("Failed to load team"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select first team when the list loads (or after create/join)
  useEffect(() => {
    if (teams.length > 0 && !selectedTeam) {
      loadTeam(teams[0].id);
    }
  }, [teams, selectedTeam, loadTeam]);

  const selectTeam = (teamId) => loadTeam(teamId);

  const refreshDrills = useCallback(async () => {
    if (!selectedTeam?.id) return;
    const res = await axios.get(`${API}/teams/${selectedTeam.id}/team-drills`, { headers });
    setDrillCatalog(res.data.catalog || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam?.id]);

  const createTeam = async () => {
    if (!teamName.trim()) return;
    try {
      await axios.post(`${API}/teams/create`, { name: teamName }, { headers });
      toast.success(`Team "${teamName}" created!`);
      setShowCreate(false);
      setTeamName("");
      await fetchTeams();
    } catch { toast.error("Failed to create team"); }
  };

  const joinTeam = async () => {
    if (!inviteCode.trim()) return;
    try {
      await axios.post(`${API}/teams/join`, { invite_code: inviteCode }, { headers });
      toast.success("Joined team!");
      setShowJoin(false);
      setInviteCode("");
      await fetchTeams();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to join"); }
  };

  const copyInvite = () => {
    if (selectedTeam?.invite_code) {
      navigator.clipboard.writeText(selectedTeam.invite_code);
      toast.success("Invite code copied!");
    }
  };

  const getMyUserId = () => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.user_id || null;
    } catch { return null; }
  };
  const myUserId = getMyUserId();
  const isCaptain = !!(selectedTeam && myUserId && selectedTeam.captain_id === myUserId);

  const primaryColor = selectedTeam?.primary_color || "#FF4500";
  const secondaryColor = selectedTeam?.secondary_color || "#CCFF00";

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative" data-testid="teams-page">
      <div className="noise-overlay" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">Compete Together</p>
            <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">Teams</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowCreate(true)} className="bg-[#FF4500] hover:bg-[#FF6326] font-heading uppercase tracking-wider text-xs rounded-none" data-testid="create-team-btn">
              <Plus className="w-4 h-4 mr-1" />Create
            </Button>
            <Button onClick={() => setShowJoin(true)} variant="outline" className="border-white/10 text-white/50 hover:bg-white hover:text-black font-heading uppercase tracking-wider text-xs rounded-none" data-testid="join-team-btn">
              Join
            </Button>
          </div>
        </div>

        {/* Create/Join modals */}
        {showCreate && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="create-team-modal">
            <p className="text-sm font-heading text-white uppercase mb-3">Create Team</p>
            <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team name..." className="w-full bg-black border border-white/10 text-white p-3 mb-3 text-sm font-mono focus:border-[#FF4500] outline-none" data-testid="team-name-input" />
            <div className="flex gap-2">
              <Button onClick={createTeam} className="bg-[#FF4500] rounded-none font-heading text-xs" data-testid="confirm-create-btn">Create</Button>
              <Button onClick={() => setShowCreate(false)} variant="ghost" className="text-white/40 rounded-none text-xs">Cancel</Button>
            </div>
          </div>
        )}
        {showJoin && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="join-team-modal">
            <p className="text-sm font-heading text-white uppercase mb-3">Join Team</p>
            <input value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} placeholder="INVITE CODE" className="w-full bg-black border border-white/10 text-white p-3 mb-3 text-sm font-mono tracking-[0.3em] text-center focus:border-[#FF4500] outline-none" maxLength={6} data-testid="invite-code-input" />
            <div className="flex gap-2">
              <Button onClick={joinTeam} className="bg-[#FF4500] rounded-none font-heading text-xs" data-testid="confirm-join-btn">Join</Button>
              <Button onClick={() => setShowJoin(false)} variant="ghost" className="text-white/40 rounded-none text-xs">Cancel</Button>
            </div>
          </div>
        )}

        {/* No teams state */}
        {!loading && teams.length === 0 && (
          <div className="text-center py-20 border border-white/[0.06] bg-[#0A0A0A]">
            <Users className="w-12 h-12 mx-auto text-white/10 mb-4" />
            <p className="text-white/40 font-mono mb-2">No teams yet.</p>
            <p className="text-white/30 font-mono text-xs">Create or join a squad to unlock cooperative drills + weekly rivals.</p>
          </div>
        )}

        {/* Team view */}
        {selectedTeam && (
          <>
            {/* Banner with team identity */}
            <div
              className="relative border border-white/[0.06] p-6 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}26 0%, #0A0A0A 60%)`,
                borderLeft: `4px solid ${primaryColor}`,
              }}
              data-testid="team-banner"
            >
              <div className="flex items-start gap-4">
                {selectedTeam.mascot_url ? (
                  <img
                    src={`${BACKEND_URL}${selectedTeam.mascot_url}`}
                    alt="mascot"
                    className="w-20 h-20 object-contain border-2"
                    style={{ borderColor: primaryColor }}
                    data-testid="team-mascot-img"
                  />
                ) : (
                  <div
                    className="w-20 h-20 flex items-center justify-center border-2"
                    style={{ borderColor: primaryColor, color: primaryColor }}
                  >
                    <Sparkles className="w-8 h-8" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-3xl font-heading text-white uppercase truncate">{selectedTeam.name}</h2>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] mt-1" style={{ color: primaryColor }}>
                    {selectedTeam.members?.length} {selectedTeam.members?.length === 1 ? "MEMBER" : "MEMBERS"} · CAPTAIN&apos;S SQUAD
                  </p>
                  {selectedTeam.chant && (
                    <p className="mt-3 text-base italic text-white/80 border-l-2 pl-3" style={{ borderColor: secondaryColor }}>
                      “{selectedTeam.chant}”
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em]">INVITE</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-sm tracking-[0.3em]" style={{ color: secondaryColor }}>
                      {selectedTeam.invite_code}
                    </span>
                    <button onClick={copyInvite} className="text-white/30 hover:text-white" data-testid="copy-invite-btn">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-px bg-white/[0.06] overflow-x-auto" data-testid="team-tabs">
              {TAB_LIST.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`flex-1 min-w-[110px] px-4 py-3 flex items-center justify-center gap-2 text-xs font-heading uppercase tracking-wider transition-all ${
                      tab === t.id ? "bg-[#0A0A0A] text-white" : "bg-[#050505] text-white/40 hover:text-white/70"
                    }`}
                    style={tab === t.id ? { borderBottom: `2px solid ${primaryColor}` } : {}}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {tab === "squad" && <SquadView team={selectedTeam} primaryColor={primaryColor} />}
                {tab === "drills" && (
                  <TeamDrillsView
                    team={selectedTeam}
                    catalog={drillCatalog}
                    isCaptain={isCaptain}
                    refresh={refreshDrills}
                    headers={headers}
                    primaryColor={primaryColor}
                  />
                )}
                {tab === "rival" && <RivalCard headers={headers} variant="full" />}
                {tab === "identity" && (
                  <IdentityView
                    key={selectedTeam.id}
                    team={selectedTeam}
                    isCaptain={isCaptain}
                    headers={headers}
                    reload={() => selectTeam(selectedTeam.id)}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </motion.div>
    </div>
  );
};

// ============================================================
// Squad tab — member grid
// ============================================================
const SquadView = ({ team, primaryColor }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06]" data-testid="squad-grid">
    {team.members?.map((m, i) => (
      <div key={i} className="bg-[#0A0A0A] p-4 text-center" data-testid={`member-${i}`}>
        <div className="w-12 h-12 mx-auto flex items-center justify-center mb-2" style={{ background: `${primaryColor}33`, color: primaryColor }}>
          <span className="font-heading text-lg">{m.name?.[0]}</span>
        </div>
        <p className="text-sm text-white truncate font-medium">{m.name}</p>
        <p className="text-[9px] font-mono text-white/30 mt-1">{m.workouts || 0} workouts · {m.streak || 0}d streak</p>
        {m.role === "captain" && <Crown className="w-3 h-3 mx-auto text-yellow-400 mt-1" />}
      </div>
    ))}
  </div>
);

// ============================================================
// Team Drills tab — cooperative drills with member contributions
// ============================================================
const TeamDrillsView = ({ team, catalog, isCaptain, refresh, headers, primaryColor }) => {
  const [logging, setLogging] = useState({});
  const [logValue, setLogValue] = useState({});

  const start = async (templateId) => {
    try {
      await axios.post(`${API}/teams/${team.id}/team-drills/start`, { template_id: templateId }, { headers });
      toast.success("Drill started! Get the squad moving.");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  const submit = async (sessionId, templateId) => {
    const v = parseFloat(logValue[sessionId]);
    if (!v || v <= 0) return toast.error("Enter your contribution");
    setLogging({ ...logging, [sessionId]: true });
    try {
      const res = await axios.post(
        `${API}/teams/${team.id}/team-drills/${sessionId}/log`,
        { value: v }, { headers }
      );
      if (res.data.completed) {
        toast.success("🏆 DRILL COMPLETE — squad earned 3x points!", { duration: 5000 });
      } else {
        toast.success(`Logged. Squad at ${Math.round(res.data.current)}/${res.data.target}`);
      }
      setLogValue({ ...logValue, [sessionId]: "" });
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to log"); }
    finally { setLogging({ ...logging, [sessionId]: false }); }
  };

  if (!catalog.length) {
    return <div className="text-center py-10 text-white/40 font-mono text-sm">No drills available</div>;
  }

  return (
    <div className="space-y-4" data-testid="team-drills-list">
      {catalog.map(({ template: t, active_session: active }) => {
        const Icon = DRILL_ICONS[t.icon] || Target;
        const tplColor = t.color || primaryColor;
        const pct = active ? Math.min(100, Math.round((active.current / t.target) * 100)) : 0;
        return (
          <div key={t.id} className="bg-[#0A0A0A] border border-white/[0.06] p-5" data-testid={`team-drill-${t.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{ background: `${tplColor}22`, color: tplColor }}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="text-lg font-heading text-white uppercase">{t.name}</h3>
                  {active ? (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5" style={{ background: `${tplColor}22`, color: tplColor }}>
                      LIVE
                    </span>
                  ) : (
                    <span className="text-[9px] font-mono uppercase tracking-wider text-white/30">{t.min_members}+ MEMBERS</span>
                  )}
                </div>
                <p className="text-xs italic text-white/60 mb-1">{t.tagline}</p>
                <p className="text-xs text-white/40 mb-3">{t.description}</p>

                {active ? (
                  <>
                    <div className="flex items-center justify-between text-xs font-mono mb-1">
                      <span className="text-white/40">
                        SQUAD: <span style={{ color: tplColor }}>{Math.round(active.current || 0)}</span> / {t.target} {t.unit}
                      </span>
                      <span style={{ color: tplColor }}>{pct}%</span>
                    </div>
                    <div className="h-2 bg-white/[0.06] overflow-hidden mb-3">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: tplColor }} />
                    </div>

                    {/* Contributions list */}
                    {active.contributions?.length > 0 && (
                      <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                        {active.contributions.map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px] font-mono text-white/50">
                            <span>· {c.user_name}</span>
                            <span style={{ color: tplColor }}>{c.value} {t.unit}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Log contribution form */}
                    <div className="flex gap-2 mt-2">
                      <input
                        type="number"
                        value={logValue[active.id] || ""}
                        onChange={(e) => setLogValue({ ...logValue, [active.id]: e.target.value })}
                        placeholder={`Your ${t.unit}`}
                        className="flex-1 bg-black border border-white/10 text-white p-2 text-sm font-mono focus:border-white outline-none"
                        data-testid={`log-input-${t.id}`}
                        min="0"
                      />
                      <Button
                        onClick={() => submit(active.id, t.id)}
                        disabled={logging[active.id]}
                        className="rounded-none font-heading uppercase tracking-wider text-xs px-4"
                        style={{ background: tplColor, color: "#000" }}
                        data-testid={`log-submit-${t.id}`}
                      >
                        {logging[active.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : "LOG"}
                      </Button>
                    </div>
                  </>
                ) : (
                  isCaptain ? (
                    <Button
                      onClick={() => start(t.id)}
                      className="rounded-none font-heading uppercase tracking-wider text-xs mt-1"
                      style={{ background: tplColor, color: "#000" }}
                      data-testid={`start-team-drill-${t.id}`}
                    >
                      START DRILL
                    </Button>
                  ) : (
                    <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider">Waiting for captain to start</p>
                  )
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// Identity tab — captain customizes colors, chant, mascot
// ============================================================
const IdentityView = ({ team, isCaptain, headers, reload }) => {
  const [primary, setPrimary] = useState(team.primary_color || "#FF4500");
  const [secondary, setSecondary] = useState(team.secondary_color || "#CCFF00");
  const [chant, setChant] = useState(team.chant || "");
  const [vibe, setVibe] = useState(team.mascot_vibe || "");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/teams/${team.id}/identity`,
        { primary_color: primary, secondary_color: secondary, chant }, { headers });
      toast.success("Team identity updated");
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const generateMascot = async () => {
    if (!vibe.trim()) return toast.error("Describe the mascot vibe first (e.g. 'wolf pack')");
    setGenerating(true);
    try {
      await axios.post(`${API}/teams/${team.id}/mascot/generate`,
        { vibe: vibe.trim() }, { headers });
      toast.success("Mascot generated! Look at your banner.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mascot generation failed");
    } finally { setGenerating(false); }
  };

  if (!isCaptain) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-6 text-center">
        <Crown className="w-8 h-8 mx-auto text-white/20 mb-2" />
        <p className="text-white/40 font-mono text-sm">Only the captain can edit team identity.</p>
        {team.chant && (
          <p className="text-white/70 italic mt-3 text-sm">“{team.chant}”</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] p-6 space-y-6" data-testid="identity-editor">
      {/* Colors */}
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
          <Palette className="w-3 h-3" /> COLORS
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-white/60 block mb-1">Primary</label>
            <div className="flex items-center gap-2">
              <input
                type="color" value={primary}
                onChange={(e) => setPrimary(e.target.value)}
                className="w-12 h-10 bg-black border border-white/10 cursor-pointer"
                data-testid="primary-color-input"
              />
              <span className="font-mono text-xs text-white/60">{primary}</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">Secondary</label>
            <div className="flex items-center gap-2">
              <input
                type="color" value={secondary}
                onChange={(e) => setSecondary(e.target.value)}
                className="w-12 h-10 bg-black border border-white/10 cursor-pointer"
                data-testid="secondary-color-input"
              />
              <span className="font-mono text-xs text-white/60">{secondary}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chant */}
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
          <Megaphone className="w-3 h-3" /> WAR CHANT
        </p>
        <input
          type="text" value={chant}
          onChange={(e) => setChant(e.target.value)}
          maxLength={120}
          placeholder="e.g. Wolves never sleep."
          className="w-full bg-black border border-white/10 text-white p-3 text-sm focus:border-[#FF4500] outline-none"
          data-testid="chant-input"
        />
        <p className="text-[9px] font-mono text-white/30 mt-1 text-right">{chant.length}/120</p>
      </div>

      <Button
        onClick={save} disabled={saving}
        className="bg-white text-black hover:bg-white/90 rounded-none font-heading uppercase tracking-wider text-xs w-full"
        data-testid="save-identity-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "SAVE IDENTITY"}
      </Button>

      {/* Mascot generator */}
      <div className="border-t border-white/[0.06] pt-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> MASCOT (AI MANGA)
        </p>
        <p className="text-xs text-white/50 mb-3">
          One mascot illustration in the team&apos;s primary color. Re-roll anytime.
          {team.mascot_url && <span className="block mt-1 text-[#CCFF00]">Current vibe: {team.mascot_vibe}</span>}
        </p>
        <input
          type="text" value={vibe}
          onChange={(e) => setVibe(e.target.value)}
          maxLength={80}
          placeholder="e.g. wolf pack, savage phoenix, ronin samurai"
          className="w-full bg-black border border-white/10 text-white p-3 text-sm focus:border-[#FF4500] outline-none mb-3"
          data-testid="vibe-input"
        />
        <Button
          onClick={generateMascot} disabled={generating || !vibe.trim()}
          className="bg-[#FF4500] hover:bg-[#FF6326] rounded-none font-heading uppercase tracking-wider text-xs w-full"
          data-testid="generate-mascot-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SUMMONING MASCOT...</>
          ) : team.mascot_url ? "RE-ROLL MASCOT" : "GENERATE MASCOT"}
        </Button>
      </div>
    </div>
  );
};

export default TeamsPage;
